import React, { Fragment, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import styled from '@emotion/styled';
import { uniq } from 'underscore';
import { Form, Subtext, Dropdown } from '@contently/toolkit';
import * as actions from '../../store/actions/index';
import { useCookieJar } from '../../utils/cookie_jar';
import waitForGlobal from '../../utils/wait_for_global';
import setDatetimeLocale from '../../utils/datetimeLocale';

const LocalizePlaceholderWrapper = styled('div')`
  display: flex;
  align-items: center;
  > span {
    margin-right: 5px;
  }
  [class*='DropdownContainerWrapper'] {
    min-height: auto;
    max-width: 170px;
  }
  @media screen and (max-width: 800px) {
    flex-direction: column;
    margin-bottom: 15px;
  }
`;

const LocalizeLanguageSelector = ({ saveNewPhrases, onPrefferedLangChanged }) => {
  const [preferredLang, setPreferredLang] = useCookieJar('preferred_lang', 'en-US');

  // Only allow one translation before reloading the page to prevent issues
  // with Localize taking multiple passes on copy
  const [languageHasChangedCount, setLanguageHasChangedCount] = useState(0);

  const handleLangSelection = value => {
    setPreferredLang(value);
    onPrefferedLangChanged(value);
  };

  const [selectableLanguages, setSelectableLanguages] = useState([
    {
      name: 'English',
      value: 'en-US',
      action: handleLangSelection.bind(undefined, 'en-US')
    }
  ]);

  useEffect(() => {
    // To prevent browser extensions from adding text, we add a class to #page-container and target
    // *only* that class for translation(since extensions usually append text to the body)
    document.getElementById('page-container').classList.add('js-translate-root');

    // Initialize with currentLanguage if one was saved, else just use en-US till we can choose preferred below
    // NOTE - using our own "remember language" logic, to support dynamically added langs
    waitForGlobal('Localize').then(() => {
      window.Localize.initialize({
        key: window.env.LOCALIZE_JS_API_KEY,
        targetLanguage: preferredLang,
        translateMetaTags: false,
        translateAlt: false,
        translateBody: false,
        rememberLanguage: false,
        autodetectLanguage: false,
        saveNewPhrases: window.env.PUSH_LOCALIZE_PHRASES === 'true',
        saveNewPhrasesFromSource: true,
        translateClasses: ['js-translate-root'],
        blockedClasses: [
          'ui-datepicker',
          'ui-timepicker-wrapper',
          'filter-collapse',
          'textoverlay',
          'file-name',
          'file-size',
          'intercom-app'
        ]
      });

      window.Localize.getAvailableLanguages((err, languages) => {
        if (err) {
          // eslint-disable-next-line no-console
          return console.warn('LOCALIZE ERROR: ', err);
        }
        const formattedLanguages = languages.map(lang => {
          return {
            name: lang.name,
            value: lang.code,
            action: handleLangSelection.bind(undefined, lang.code)
          };
        });
        setSelectableLanguages(
          uniq([...selectableLanguages, ...formattedLanguages], false, lang => lang.name)
        );
      });
    });
  }, []);

  useEffect(() => {
    if (languageHasChangedCount === 2) {
      window.location.reload();
      return;
    }

    waitForGlobal('Localize').then(() => {
      window.Localize.setLanguage(preferredLang);
      setDatetimeLocale(preferredLang);
      setLanguageHasChangedCount(prevCount => prevCount + 1);
    });
  }, [preferredLang]);

  const selectedLanguage = selectableLanguages.find(lang => lang.value === preferredLang);

  if (!selectedLanguage) return <Fragment />;

  // TODO: make this a div that looks like an input
  const trigger = () => <Form.Input size="small" defaultValue={selectedLanguage.name} readonly />;

  return (
    <LocalizePlaceholderWrapper>
      <Subtext>Auto-translated to:</Subtext>
      <Dropdown links={selectableLanguages} Trigger={trigger} triggerArrow />
    </LocalizePlaceholderWrapper>
  );
};

LocalizeLanguageSelector.propTypes = {
  saveNewPhrases: PropTypes.bool
};

LocalizeLanguageSelector.defaultProps = {
  saveNewPhrases: false
};

const mapDispatchToProps = dispatch => ({
  onPrefferedLangChanged: preferredLang => dispatch(actions.preferredLangChanged(preferredLang))
});

export default connect(
  () => {
    return {};
  },
  mapDispatchToProps
)(LocalizeLanguageSelector);



// WEBPACK FOOTER //
// ./app/javascript/app/components/layout/LocalizeLanguageSelector.jsx